import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import FormSelect from "../common/FormSelect";
import { DropDownBold } from "../AdminIcons";
import { useUpdateRoleMutation } from "../../services/user/userApi";
import {
  buildRoutePermissionPayload,
  mapApiRoutePermissionsToSelected,
} from "../../utils/rolePermissionMapper";
import { permissionOptions } from "../../utils/rolePermissionOptions";
import { getApiErrorMessage, showToast } from "../../utils/toast";

const UpdateRoleModal = ({ isOpen, onClose, role, onSuccess }) => {
  const [formError, setFormError] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [updateRole, { isLoading }] = useUpdateRoleMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const handleClose = () => {
    reset();
    setFormError("");
    setSelectedPermissions({});
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && role) {
      reset({
        name: role.roleName && role.roleName !== "-" ? role.roleName : "",
        description:
          role.description && role.description !== "-" ? role.description : "",
      });
      setFormError("");
      setSelectedPermissions(
        mapApiRoutePermissionsToSelected(role.routePermissions, permissionOptions),
      );
    }
  }, [isOpen, role, reset]);

  const onSubmit = async (formValues) => {
    let routePermissionArray = buildRoutePermissionPayload(selectedPermissions);

    if (
      routePermissionArray.length === 0 &&
      Array.isArray(role?.routePermissions) &&
      role.routePermissions.length > 0
    ) {
      routePermissionArray = role.routePermissions;
    }

    if (routePermissionArray.length === 0) {
      setFormError("Please select at least one permission");
      return;
    }

    try {
      setFormError("");
      await updateRole({
        roleId: String(role.id || ""),
        name: formValues.name.trim(),
        description: formValues.description.trim(),
        routePermission: routePermissionArray,
      }).unwrap();
      await onSuccess?.();
      showToast({
        type: "success",
        message: "Role updated successfully.",
      });
      handleClose();
    } catch (error) {
      setFormError(
        getApiErrorMessage(error, "Unable to update role. Please try again."),
      );
    }
  };

  if (!isOpen || !role) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden bg-black/45 p-4"
      onClick={handleClose}
    >
      <div
        className="max-h-[min(92vh,calc(100dvh-2rem))] w-full max-w-[570px] overflow-y-auto rounded-2xl border border-[#00000014] bg-[#EAE9E9] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-12 pt-8 pb-7">
          <h3 className="text-xl font-medium leading-6 text-black">Update a Role</h3>

          <form className="mt-5 space-y-6" noValidate onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label className="mb-1.5 block text-black">Enter Role Name</label>
              <input
                type="text"
                className="h-11 w-full rounded-[5px] border border-[#00000014] bg-[#D9D9D961] px-4 text-xl text-black outline-none"
                placeholder="e.g., Verifier"
                {...register("name", {
                  required: "Role name is required",
                })}
              />
              {errors.name?.message ? (
                <p className="mt-1.5 text-sm text-[#DC2626]">{errors.name.message}</p>
              ) : null}
            </div>

            <div>
              <label className="mb-1.5 block text-black">Role Description</label>
              <textarea
                rows={4}
                className="w-full rounded-[5px] border border-[#00000014] bg-[#D9D9D961] px-4 py-3 text-xl text-black outline-none"
                placeholder="e.g., Verify any account"
                {...register("description", {
                  required: "Description is required",
                })}
              />
              {errors.description?.message ? (
                <p className="mt-1.5 text-sm text-[#DC2626]">
                  {errors.description.message}
                </p>
              ) : null}
            </div>

            <div>
              <label className="mb-1.5 block text-black">Select Permissions</label>
              <FormSelect
                key={`update-role-permission-${role.id}`}
                id="update-role-permission"
                options={permissionOptions}
                initialSelectedSubOptions={selectedPermissions}
                defaultValue=""
                placeholder=""
                controlWrapperClassName="mt-0"
                selectClassName="h-11 rounded-[5px] border border-[#00000014] bg-[#D9D9D961] px-4 text-xl text-black"
                dropdownClassName="py-3 left-0 w-full text-black font-medium h-80"
                icon={DropDownBold}
                iconProps={{ color: "#3C3C3C" }}
                optionIcon={DropDownBold}
                optionIconProps={{ color: "#3C3C3C" }}
                optionClassName="px-4 py-2"
                showTotalSelectedCount
                showPerOptionSelectedCount={false}
                onSubOptionsChange={(selectedSubOptions) => {
                  setSelectedPermissions(selectedSubOptions);
                }}
              />
            </div>

            {formError ? (
              <p className="text-sm font-medium text-[#DC2626]">{formError}</p>
            ) : null}
          </form>

          <div className="mt-10 flex justify-end gap-5">
            <button
              type="button"
              className="h-8 rounded-lg bg-[#DBDBDB] px-6 text-lg font-medium text-[#686868]"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isSubmitting || isLoading}
              className="h-8 rounded-lg bg-[#923D8C] px-5 text-lg font-medium text-[#FAFCFE] disabled:cursor-not-allowed disabled:opacity-70"
              onClick={handleSubmit(onSubmit)}
            >
              {isSubmitting || isLoading ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateRoleModal;
